import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Section, { FadeIn } from './ui/Section';
import SafeIcon from '../common/SafeIcon';

const translations = [
  { phrase: "I'm Fine", mean: "I need space to process", hear: "I don't want to deal with you", say: "Take your time. I'm here when you're ready" },
  { phrase: "Do What You Want", mean: "Please choose what I want", hear: "I have no preference", say: "I want us both happy—what do you prefer?" },
  { phrase: "We Need To Talk", mean: "I miss feeling close", hear: "You're in trouble", say: "I want to connect too. When's a good time?" },
  { phrase: "I'm Not Hungry", mean: "I want to share your food", hear: "She doesn't want anything", say: "Want to split something?" },
  { phrase: "I Need Space", mean: "I'm overwhelmed, not leaving you", hear: "I want to be away from you", say: "How much time feels right?" },
  { phrase: "It's Okay", mean: "I want to move past this quickly", hear: "My feelings don't matter", say: "I want to understand why this matters to you" },
  { phrase: "I'm Tired", mean: "I'm emotionally depleted", hear: "She needs sleep", say: "Want to talk or just be held?" },
  { phrase: "Whatever You Think", mean: "I have a preference I'm hiding", hear: "She trusts my judgment", say: "I want your input—what would make you happy?" },
  { phrase: "I Miss You (3 AM)", mean: "I'm lonely and you're familiar", hear: "They want me back", say: "I miss what we had too. But why now?" },
  { phrase: "Let's Take It Slow", mean: "I'm interested but scared", hear: "They're not that into me", say: "I respect slow. What does that look like?" }
];

const TranslationCard = ({ data, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="bg-white border text-left border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
    >
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-dark text-brand-yellow font-bold text-sm">
            {index + 1}
          </span>
          <h4 className="text-xl font-bold text-brand-dark">"{data.phrase}"</h4>
        </div>
        <SafeIcon 
          name="ChevronDown" 
          className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-6 pt-0 space-y-4 border-t border-gray-100 bg-gray-50/50">
              <div className="space-y-1 mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">What they mean</p>
                <p className="text-brand-dark font-medium">{data.mean}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">What you hear</p>
                <p className="text-red-600 line-through decoration-red-200">{data.hear}</p>
              </div>
              <div className="space-y-1 bg-brand-yellow/10 p-4 rounded-lg border border-brand-yellow/20">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-dark">Exact Script: What to say back</p>
                <p className="font-bold text-brand-dark">"{data.say}"</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Translations = () => {
  return (
    <Section className="bg-brand-gray">
      <div className="max-w-3xl mx-auto text-center mb-16">
        <FadeIn>
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Your Free 14-Page Guide Includes</h2>
          <p className="text-gray-600 text-lg">Click each phrase to see a sneak peek of the translations inside the guide.</p>
        </FadeIn>
      </div>

      <div className="max-w-4xl mx-auto space-y-4">
        {translations.map((item, i) => (
          <TranslationCard key={i} data={item} index={i} />
        ))}
        
        <FadeIn delay={0.5} className="mt-8 text-center bg-brand-dark text-white p-6 rounded-xl flex items-center justify-center gap-3">
          <SafeIcon name="Smartphone" className="text-brand-yellow w-6 h-6" />
          <span className="font-medium">Plus: Quick-reference summary page you can save to your phone.</span>
        </FadeIn>
      </div>
    </Section>
  );
};

export default Translations;